import React, { useState } from 'react';
import { Plus, Trash2, Youtube, ExternalLink, AlertCircle, Check, GripVertical } from 'lucide-react';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * YouTubeGalleryEditor - Editor component for youtube-gallery block
 * Syncs with profile's youtubeVideos array
 */
const YouTubeGalleryEditor = ({ block, onChange, youtubeVideos = [], onUpdateYoutubeVideos, theme, hideStylePanels }) => {
  const { content } = block;
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [addError, setAddError] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  // Extract YouTube video ID from URL
  const extractYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match ? match[1] : null;
  };

  // Get YouTube thumbnail
  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  };

  // Add new video to profile
  const handleAddVideo = async () => {
    setAddError('');

    const videoId = extractYouTubeVideoId(newVideoUrl);
    if (!videoId) {
      setAddError('Please enter a valid YouTube URL');
      return;
    }

    // Check for duplicate
    if (youtubeVideos.some((v) => v.videoId === videoId)) {
      setAddError('This video is already in your gallery');
      return;
    }

    const newVideo = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'youtube_video',
      videoId: videoId,
      url: newVideoUrl,
      title: newVideoTitle || 'Untitled Video',
      description: '',
      thumbnailUrl: getYouTubeThumbnail(videoId),
      addedTimestamp: Date.now(),
    };

    try {
      setIsAdding(true);
      await onUpdateYoutubeVideos([...youtubeVideos, newVideo]);
      setNewVideoUrl('');
      setNewVideoTitle('');
    } catch (err) {
      setAddError('Failed to add video');
    } finally {
      setIsAdding(false);
    }
  };

  // Remove video from profile
  const handleRemoveVideo = async (videoId) => {
    try {
      const updatedVideos = youtubeVideos.filter((v) => v.id !== videoId);
      await onUpdateYoutubeVideos(updatedVideos);

      // Also remove from selectedVideoIds if present
      if (!content.showAll && content.selectedVideoIds?.includes(videoId)) {
        handleChange('selectedVideoIds', content.selectedVideoIds.filter((id) => id !== videoId));
      }
    } catch (err) {
      console.error('Failed to remove video:', err);
    }
  };

  // Toggle video selection (for showAll = false mode)
  const toggleVideoSelection = (videoId) => {
    const currentSelection = content.selectedVideoIds || [];
    const isSelected = currentSelection.includes(videoId);

    if (isSelected) {
      handleChange('selectedVideoIds', currentSelection.filter((id) => id !== videoId));
    } else {
      handleChange('selectedVideoIds', [...currentSelection, videoId]);
    }
  };

  const isVideoSelected = (videoId) => {
    if (content.showAll) return true;
    return (content.selectedVideoIds || []).includes(videoId);
  };

  return (
    <div className="space-y-4">
      {/* Section Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Section Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="My Videos"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Layout */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Layout
        </label>
        <div className="flex gap-2">
          {[
            { value: 'grid', label: 'Grid' },
            { value: 'featured', label: 'Featured' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleChange('layout', option.value)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                content.layout === option.value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Show All / Select Videos Toggle */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Videos to Display
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => handleChange('showAll', true)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              content.showAll
                ? 'bg-[#749555] text-white'
                : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
            }`}
          >
            Show All
          </button>
          <button
            onClick={() => handleChange('showAll', false)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              !content.showAll
                ? 'bg-[#749555] text-white'
                : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
            }`}
          >
            Select Videos
          </button>
        </div>
      </div>

      {/* Max Videos (only if showAll) */}
      {content.showAll && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Max Videos to Show (0 = all)
          </label>
          <input
            type="number"
            min="0"
            max="20"
            value={content.maxVideos || 0}
            onChange={(e) => handleChange('maxVideos', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white focus:border-[#749555] focus:outline-none"
          />
        </div>
      )}

      {/* Display Options */}
      <div className="flex gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={content.showTitles !== false}
            onChange={(e) => handleChange('showTitles', e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 bg-[#1a1a2e] text-[#749555] focus:ring-[#749555]"
          />
          <span className="text-sm text-gray-300">Show Titles</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={content.showDescriptions}
            onChange={(e) => handleChange('showDescriptions', e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 bg-[#1a1a2e] text-[#749555] focus:ring-[#749555]"
          />
          <span className="text-sm text-gray-300">Show Descriptions</span>
        </label>
      </div>

      {/* Video List */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-300">
            Your Videos ({youtubeVideos.length})
          </label>
          {!content.showAll && (
            <span className="text-xs text-gray-500">
              {(content.selectedVideoIds || []).length} selected
            </span>
          )}
        </div>

        {youtubeVideos.length === 0 ? (
          <div className="text-center py-8 bg-[#1a1a2e] border border-gray-700 rounded-lg">
            <Youtube size={32} className="mx-auto text-gray-600 mb-2" />
            <p className="text-gray-500 text-sm">No videos yet</p>
            <p className="text-gray-600 text-xs">Add your first video below</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {youtubeVideos.map((video) => (
              <div
                key={video.id}
                className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${
                  isVideoSelected(video.id)
                    ? 'bg-[#749555]/10 border-[#749555]/50'
                    : 'bg-[#1a1a2e] border-gray-700'
                }`}
              >
                {/* Selection checkbox (only if not showAll) */}
                {!content.showAll && (
                  <button
                    onClick={() => toggleVideoSelection(video.id)}
                    className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                      isVideoSelected(video.id)
                        ? 'bg-[#749555] text-white'
                        : 'border border-gray-600 text-transparent'
                    }`}
                  >
                    <Check size={14} />
                  </button>
                )}

                {/* Thumbnail */}
                <img
                  src={video.thumbnailUrl || getYouTubeThumbnail(video.videoId)}
                  alt={video.title}
                  className="w-16 h-10 object-cover rounded flex-shrink-0"
                />

                {/* Title */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{video.title}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <a
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 text-gray-400 hover:text-white transition-colors"
                    title="Open video"
                  >
                    <ExternalLink size={14} />
                  </a>
                  <button
                    onClick={() => handleRemoveVideo(video.id)}
                    className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                    title="Remove video"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add New Video */}
      <div className="pt-4 border-t border-gray-700">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Add New Video
        </label>

        <div className="space-y-2">
          <input
            type="text"
            value={newVideoUrl}
            onChange={(e) => {
              setNewVideoUrl(e.target.value);
              setAddError('');
            }}
            placeholder="Paste YouTube URL..."
            className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
          />

          {extractYouTubeVideoId(newVideoUrl) && (
            <input
              type="text"
              value={newVideoTitle}
              onChange={(e) => setNewVideoTitle(e.target.value)}
              placeholder="Video title..."
              className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
            />
          )}

          {addError && (
            <p className="text-red-400 text-sm flex items-center gap-1">
              <AlertCircle size={14} />
              {addError}
            </p>
          )}

          <button
            onClick={handleAddVideo}
            disabled={!newVideoUrl || isAdding}
            className="w-full py-2.5 bg-[#749555] hover:bg-[#5c7a43] disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            {isAdding ? 'Adding...' : 'Add Video'}
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-2">
          Videos added here will also appear in your profile's YouTube tab
        </p>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
      <SizeSpacingPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['youtube-gallery']}
      />
      <ColorsFontsPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['youtube-gallery']}
        theme={theme || {}}
        blockType="youtube-gallery"
      />
      </>)}
    </div>
  );
};

export default YouTubeGalleryEditor;
